// pages/IdentityList/IdentityList.js
// Top-level identity listing — all active identity profiles with rich cards.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Fingerprint, RefreshCw, Search, ChevronLeft, ChevronRight,
  Users, Eye, Monitor, Clock, ArrowUpDown, GitMerge, Shield, Camera,
  Zap, Cpu, Target, Brain, Layers, Activity,
} from 'lucide-react';
import { ggosDeviceService } from '../../services/api';
import FaceImg from '../../components/FaceImg';
import './IdentityList.css';

const SORT_OPTIONS = [
  { value: 'last_seen_at', label: 'Last Seen' },
  { value: 'total_sessions', label: 'Fragments' },
  { value: 'total_impressions', label: 'Impressions' },
  { value: 'confidence', label: 'Confidence' },
  { value: 'created_at', label: 'Created' },
];

const fmtRel = (d) => {
  if (!d) return '\u2014';
  const ms = Date.now() - new Date(d).getTime();
  if (ms < 60000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ago`;
  if (ms < 86400000) return `${Math.floor(ms / 3600000)}h ago`;
  return `${Math.floor(ms / 86400000)}d ago`;
};

const shortId = (id) => id ? id.substring(0, 8) : '\u2014';

/* Quick identity strength (lightweight version for list view) */
function quickStrength(id) {
  let s = 0;
  if (id.hasOracle) s += 30;
  s += Math.min(id.exemplarCount || 0, 3) * 8;
  s += Math.min((id.deviceCount || 0) - 1, 3) * 10;
  s += Math.min(id.rideCount || 0, 100) * 0.15;
  s += Math.min(id.mergedCount || 0, 20) * 0.5;
  s += (id.confidence || 0) * 5;
  return Math.min(100, Math.round(s));
}

function strengthColor(s) {
  if (s >= 80) return '#16a34a';
  if (s >= 60) return '#2563eb';
  if (s >= 40) return '#d97706';
  if (s >= 20) return '#ea580c';
  return '#dc2626';
}

export default function IdentityList() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState('last_seen_at');
  const [dir, setDir] = useState('desc');

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 50, sort, dir });
      if (debouncedSearch) params.set('search', debouncedSearch);
      const json = await ggosDeviceService.getIdentities(Object.fromEntries(params));
      if (!json.success) throw new Error(json.error);
      setData(json.data);
    } catch (err) {
      console.error('Failed to fetch identities:', err);
    } finally {
      setLoading(false);
    }
  }, [page, sort, dir, debouncedSearch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSort = (col) => {
    if (sort === col) {
      setDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSort(col);
      setDir('desc');
    }
    setPage(0);
  };

  const identities = data?.identities || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  // Fleet-wide stats
  const fleetStats = useMemo(() => {
    if (!identities.length) return null;
    const withOracle = identities.filter(i => i.hasOracle).length;
    const multiDevice = identities.filter(i => i.deviceCount > 1).length;
    const totalRides = identities.reduce((s, i) => s + (i.rideCount || 0), 0);
    const totalMerged = identities.reduce((s, i) => s + (i.mergedCount || 0), 0);
    return { withOracle, multiDevice, totalRides, totalMerged };
  }, [identities]);

  return (
    <div className="il-page">
      <div className="il-header">
        <div className="il-header-left">
          <Fingerprint size={20} className="il-header-icon" />
          <h2>Identities</h2>
          <span className="il-count">{total}</span>
        </div>
        <div className="il-header-right">
          <div className="il-search">
            <Search size={14} />
            <input
              type="text"
              placeholder="Search by ID..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button className="il-refresh" onClick={fetchData} disabled={loading}>
            <RefreshCw size={13} className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Fleet summary bar */}
      {fleetStats && (
        <div className="il-fleet-bar">
          <div className="il-fleet-stat">
            <Zap size={12} />
            <span className="il-fleet-val">{fleetStats.withOracle}</span>
            <span className="il-fleet-lbl">Oracle</span>
          </div>
          <div className="il-fleet-stat">
            <Monitor size={12} />
            <span className="il-fleet-val">{fleetStats.multiDevice}</span>
            <span className="il-fleet-lbl">Multi-Device</span>
          </div>
          <div className="il-fleet-stat">
            <Users size={12} />
            <span className="il-fleet-val">{fleetStats.totalRides.toLocaleString()}</span>
            <span className="il-fleet-lbl">Rides (page)</span>
          </div>
          <div className="il-fleet-stat">
            <GitMerge size={12} />
            <span className="il-fleet-val">{fleetStats.totalMerged}</span>
            <span className="il-fleet-lbl">Merges</span>
          </div>
        </div>
      )}

      {/* Card grid */}
      {identities.length === 0 && !loading ? (
        <div className="il-empty">
          <Fingerprint size={32} />
          <p>No identities found</p>
        </div>
      ) : (
        <div className="il-grid">
          {identities.map(id => {
            const strength = quickStrength(id);
            const sColor = strengthColor(strength);
            const isGhost = !id.hasCrop && !id.hasOracle;
            return (
              <Link
                key={id.unifiedId}
                to={`/ggos-devices/identity/${id.unifiedId}`}
                className={`il-card${isGhost ? ' il-card--ghost' : ''}`}
              >
                {/* Top row: photo + identity info + strength ring */}
                <div className="il-card-top">
                  {id.faceCropUrl ? (
                    <FaceImg src={id.faceCropUrl} alt="" className="il-card-photo" />
                  ) : (
                    <div className="il-card-photo-placeholder"><Fingerprint size={20} /></div>
                  )}
                  <div className="il-card-info">
                    <div className="il-card-name-row">
                      <span className="il-card-uid">{shortId(id.unifiedId)}</span>
                      {id.mergedCount > 0 && (
                        <span className="il-card-merge-badge" title={`${id.mergedCount} profiles merged in`}>
                          <GitMerge size={9} /> {id.mergedCount}
                        </span>
                      )}
                    </div>
                    <span className="il-card-id-full" title={id.unifiedId}>{id.unifiedId}</span>
                    {id.demographics ? (
                      <span className="il-card-demo" title={`gated aggregate: n=${id.demographics.nGender}`}>
                        {id.demographics.gender}{id.demographics.ageMedian != null ? `, ${id.demographics.ageMedian}y` : ''}
                      </span>
                    ) : (
                      <span className="il-card-demo" style={{ opacity: 0.5 }} title="Below quality floor (n<5 gated samples)">demographics: insufficient data</span>
                    )}
                  </div>
                  {/* Mini strength ring */}
                  <div className="il-card-strength" title={`Identity Strength: ${strength}`}>
                    <svg viewBox="0 0 36 36" className="il-strength-ring">
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none" stroke="#eee" strokeWidth="3.5" />
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none" stroke={sColor} strokeWidth="3.5"
                        strokeDasharray={`${strength}, 100`}
                        strokeLinecap="round" />
                    </svg>
                    <span className="il-strength-val" style={{ color: sColor }}>{strength}</span>
                  </div>
                </div>

                {/* Badge row */}
                <div className="il-card-badge-row">
                  {id.hasOracle ? (
                    <span className="il-badge il-badge--oracle"><Zap size={9} /> Oracle</span>
                  ) : (
                    <span className="il-badge il-badge--pending"><Cpu size={9} /> Pending</span>
                  )}
                  {id.deviceCount > 1 && (
                    <span className="il-badge il-badge--multi"><Monitor size={9} /> {id.deviceCount}dev</span>
                  )}
                  {id.exemplarCount > 0 && (
                    <span className="il-badge il-badge--exemplar"><Layers size={9} /> {id.exemplarCount}ex</span>
                  )}
                  {id.confidence ? (
                    <span className="il-badge il-badge--conf">{(id.confidence * 100).toFixed(0)}%</span>
                  ) : null}
                </div>

                {/* Stats row */}
                <div className="il-card-stats">
                  <div className="il-card-stat" title={`${id.totalSessions} raw fragments`}>
                    <Activity size={11} />
                    <span className="il-stat-val">{id.rideCount}</span>
                    <span className="il-card-stat-lbl">rides</span>
                  </div>
                  <div className="il-card-stat">
                    <Eye size={11} />
                    <span className="il-stat-val">{id.totalImpressions}</span>
                    <span className="il-card-stat-lbl">impr</span>
                  </div>
                  <div className="il-card-stat">
                    <Monitor size={11} />
                    <span className="il-stat-val">{id.deviceCount}</span>
                    <span className="il-card-stat-lbl">devices</span>
                  </div>
                  {id.faceIdCount > 0 && (
                    <div className="il-card-stat">
                      <Fingerprint size={11} />
                      <span className="il-stat-val">{id.faceIdCount}</span>
                      <span className="il-card-stat-lbl">IDs</span>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="il-card-footer">
                  <span><Clock size={10} /> {fmtRel(id.lastSeenAt)}</span>
                  <div className="il-card-status-icons">
                    {id.hasCrop && <span className="il-status-dot il-status-dot--crop" title="Face crop"><Camera size={10} /></span>}
                    {id.expressionProfile?.dominant && (
                      <span className="il-card-mood">{id.expressionProfile.dominant}</span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Sort bar + Pagination */}
      <div className="il-footer">
        <div className="il-sort">
          {SORT_OPTIONS.map(o => (
            <button
              key={o.value}
              className={`il-sort-btn${sort === o.value ? ' il-sort-btn--active' : ''}`}
              onClick={() => toggleSort(o.value)}
            >
              {o.label}
              {sort === o.value && (
                <ArrowUpDown size={10} style={{ transform: dir === 'asc' ? 'scaleY(-1)' : 'none' }} />
              )}
            </button>
          ))}
        </div>
        {totalPages > 1 && (
          <div className="il-pagination">
            <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft size={14} />
            </button>
            <span>{page + 1} / {totalPages}</span>
            <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
